import type { ParsedParam } from "@/lib/mcp/schema";

/**
 * Validation error result
 */
export type ValidationResult = {
  isValid: boolean;
  error?: string;
};

/**
 * Validate a parameter value against its schema
 */
export function validateParameterValue(
  value: string,
  param: ParsedParam,
): ValidationResult {
  // Empty values are allowed for optional parameters
  if (value.trim() === "" && !param.required) {
    return { isValid: true };
  }

  // Required parameters cannot be empty
  if (value.trim() === "" && param.required) {
    return { isValid: false, error: "Required" };
  }

  switch (param.type) {
    case "boolean": {
      if (value !== "true" && value !== "false") {
        return { isValid: false, error: "Must be true or false" };
      }
      break;
    }
    case "integer":
    case "number": {
      const num = Number(value);
      if (Number.isNaN(num)) {
        return { isValid: false, error: "Must be a number" };
      }

      if (param.type === "integer" && !Number.isInteger(num)) {
        return { isValid: false, error: "Must be an integer" };
      }

      if (param.minimum !== undefined && num < param.minimum) {
        return { isValid: false, error: `Min ${param.minimum}` };
      }

      if (param.maximum !== undefined && num > param.maximum) {
        return { isValid: false, error: `Max ${param.maximum}` };
      }
      break;
    }
    case "object":
    case "array": {
      try {
        const val = JSON.parse(value);
        if (
          param.type === "object" &&
          (val === null || typeof val !== "object" || Array.isArray(val))
        ) {
          return { isValid: false, error: "Must be a JSON object" };
        }
        if (param.type === "array" && !Array.isArray(val)) {
          return { isValid: false, error: "Must be a JSON array" };
        }
      } catch {
        return { isValid: false, error: "Invalid JSON" };
      }
      break;
    }
    default:
      // String and unknown types don't need special validation
      break;
  }

  return { isValid: true };
}

/**
 * Coerce a string parameter value to its appropriate type
 */
export function coerceParameterValue(
  value: string,
  param: ParsedParam,
): { success: boolean; value?: unknown; error?: string } {
  // Handle empty values
  if (value.trim() === "") {
    if (param.required && param.defaultValue === undefined) {
      return { success: false, error: "Required" };
    }
    return { success: true, value: param.defaultValue };
  }

  try {
    switch (param.type) {
      case "boolean":
        return { success: true, value: value === "true" };

      case "integer": {
        const n = Number(value);
        if (!Number.isInteger(n)) {
          return { success: false, error: "Must be integer" };
        }
        return { success: true, value: n };
      }

      case "number": {
        const n = Number(value);
        if (Number.isNaN(n)) {
          return { success: false, error: "Must be number" };
        }
        return { success: true, value: n };
      }

      case "object":
      case "array": {
        const val = JSON.parse(value);
        if (
          param.type === "object" &&
          (val === null || typeof val !== "object" || Array.isArray(val))
        ) {
          return { success: false, error: "Must be JSON object" };
        }
        if (param.type === "array" && !Array.isArray(val)) {
          return { success: false, error: "Must be JSON array" };
        }
        return { success: true, value: val };
      }

      default:
        return { success: true, value };
    }
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

/**
 * Coerce all parameters for execution
 */
export function coerceAllParameters(
  paramValues: Record<string, string>,
  paramSchemas: ParsedParam[],
): { args: Record<string, unknown>; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const args: Record<string, unknown> = {};

  for (const param of paramSchemas) {
    const rawValue = paramValues[param.name];
    const result = coerceParameterValue(rawValue || "", param);

    if (!result.success) {
      errors[param.name] = result.error || "Invalid value";
    } else if (result.value !== undefined) {
      args[param.name] = result.value;
    }
  }

  return { args, errors };
}
